/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import useGamePlayerRoster from '@sportninja/common/hooks/useGamePlayerRoster'
import req from '@sportninja/common/api/request'
import { getSoccerSport } from '@sportninja/common/selectors/types'
import { ManageTeamAttendanceModalProvider } from './context'
import ManageTeamAttendanceModalContent from './ManageTeamAttendanceModalContent'
import ModalHeader from './components/ModalHeader'
import ModalFooter from './components/ModalFooter'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type ManageTeamAttendanceModalProps = {
  isManageAttendanceModalOpen: boolean
  setIsManageAttendanceModalOpen: (isOpen: boolean) => void
  selectedManageAttendance: {
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
  } | null
  readAttendanceInformation: () => void
  soccerSportId: number
}

/** Inner component that has access to context and only mounts when modal is open */
const ManageTeamAttendanceModalInner: React.FC<{
  isOpen: boolean
  onClose: () => void
  selectedManageAttendance: ManageTeamAttendanceModalProps['selectedManageAttendance']
  readAttendanceInformation: () => void
  soccerSportId: number
}> = ({
  isOpen,
  onClose,
  selectedManageAttendance,
  readAttendanceInformation,
  soccerSportId,
}) => {
  const { roster } = useGamePlayerRoster(
    selectedManageAttendance?.gameId,
    selectedManageAttendance?.rosterId
  )
  const [settings, setSettings] = useState<any>({})
  const [game, setGame] = useState<any>(null)

  const isGenericSport = useMemo(
    () => game?.generic_sport_id !== null,
    [game?.generic_sport_id]
  )
  const isSoccer = useMemo(
    () => game?.sport_id === soccerSportId,
    [game?.sport_id, soccerSportId]
  )
  const requireUniqueJerseyNumbers =
    settings?.settings?.game?.require_unique_jersey_numbers

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await req(
          `/games/${selectedManageAttendance?.gameId}/settings`
        )
        setSettings(data)
      } catch (e) {
        console.error(e)
      }
    }
    if (selectedManageAttendance?.gameId) {
      fetchSettings()
    }
  }, [selectedManageAttendance?.gameId])

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const { data } = await req(`/games/${selectedManageAttendance?.gameId}`)
        setGame(data)
      } catch (e) {
        console.error(e)
      }
    }
    if (selectedManageAttendance?.gameId) {
      fetchGame()
    }
  }, [selectedManageAttendance?.gameId])

  const handleClose = useCallback(() => {
    readAttendanceInformation()
    onClose()
  }, [readAttendanceInformation, onClose])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  return (
    <Modal
      open={isOpen}
      onOk={handleClose}
      onCancel={handleClose}
      css={css`
        .ant-modal-wrap {
          z-index: 10001;
        }
      `}
      width={650}
      centered
      styles={modalStyles}
      maskClosable={false}
      footer={<ModalFooter onClose={handleClose} />}
      title={<ModalHeader teamName={selectedManageAttendance?.teamName} />}
      closeIcon={closeIcon()}
    >
      <ManageTeamAttendanceModalContent
        players={roster?.players || []}
        gameId={roster?.game_id}
        rosterId={roster?.id}
        requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
        isGenericSport={isGenericSport}
        isSoccer={isSoccer}
      />
    </Modal>
  )
}

const ManageTeamAttendanceModal: React.FC<ManageTeamAttendanceModalProps> = ({
  isManageAttendanceModalOpen,
  setIsManageAttendanceModalOpen,
  selectedManageAttendance,
  readAttendanceInformation,
  soccerSportId,
}) => {
  const handleClose = useCallback(() => {
    setIsManageAttendanceModalOpen(false)
  }, [setIsManageAttendanceModalOpen])

  if (!isManageAttendanceModalOpen) {
    return null
  }

  return (
    <ManageTeamAttendanceModalProvider>
      <ManageTeamAttendanceModalInner
        isOpen={isManageAttendanceModalOpen}
        onClose={handleClose}
        selectedManageAttendance={selectedManageAttendance}
        readAttendanceInformation={readAttendanceInformation}
        soccerSportId={soccerSportId}
      />
    </ManageTeamAttendanceModalProvider>
  )
}

const mapStateToProps = (state) => {
  return {
    soccerSportId: getSoccerSport(state)?.id,
  }
}
export default connect(mapStateToProps)(ManageTeamAttendanceModal)
